import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, X } from 'lucide-react';
import PageHeader from './PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api';
import { toast } from 'sonner';
import { statusColor, formatDate, inrCompact } from '../lib/format';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';

export default function CrudPage({ overline, title, subtitle, endpoint, fields, columns, formId = null }) {
  const ff = useFormFieldHelpers(formId);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/${endpoint}`);
      setItems(Array.isArray(data) ? data : (data?.items || data?.results || data?.data || []));
    } catch (e) { setItems([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const reset = () => { setForm({}); setEditing(null); };
  const submit = async (e) => {
    e.preventDefault();
    // App-level validation (config-driven) — required fields + no negative numbers,
    // so CrudPage hubs (Contracts, HSE, Documents…) can't persist empty/garbage rows.
    const isReq = (f) => (formId && f.cfg ? ff.isReq(f.cfg, f.required || false) : !!f.required);
    const labelOf = (f) => (formId && f.cfg ? ff.labelOf(f.cfg, f.label) : f.label);
    const missing = fields.find(f => isReq(f) && (form[f.key] === undefined || form[f.key] === null || String(form[f.key]).trim() === ''));
    if (missing) { toast.error(`${labelOf(missing)} is required`); return; }
    const negative = fields.find(f => f.type === 'number' && form[f.key] !== undefined && form[f.key] !== '' && Number(form[f.key]) < 0);
    if (negative) { toast.error(`${labelOf(negative)} cannot be negative`); return; }
    const payload = { ...form };
    fields.forEach(f => { if (f.type === 'number' && payload[f.key]) payload[f.key] = Number(payload[f.key]); });
    try {
      if (editing) {
        await apiPut(`/${endpoint}/${editing}`, payload);
        toast.success('Updated');
      } else {
        await apiPost(`/${endpoint}`, payload);
        toast.success('Created');
      }
      setOpen(false); reset(); load();
    } catch (e) { toast.error('Failed to save'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this record?')) return;
    try { await apiDel(`/${endpoint}/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };
  const editItem = (it) => { setForm(it); setEditing(it.id); setOpen(true); };

  const renderCell = (col, row) => {
    const v = row[col.key];
    if (col.type === 'currency') return inrCompact(v);
    if (col.type === 'date') return formatDate(v);
    if (col.type === 'status') return <span className={`tag ${statusColor(v)}`}>{v || '—'}</span>;
    return v ?? '—';
  };

  return (
    <div className="p-6 md:p-8">
      <PageHeader
        overline={overline}
        title={title}
        subtitle={subtitle}
        actions={
          <button
            data-testid={`${endpoint}-new-btn`}
            onClick={() => { reset(); setOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] transition-all hover:-translate-y-px"
          >
            <Plus size={15} /> New
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-280px)]">
          <table className="w-full dense-table">
            <thead>
              <tr>
                {columns.map(c => <th key={c.key}>{c.label}</th>)}
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length + 1} className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="text-center py-16 text-slate-400">
                  <div className="text-xs uppercase tracking-widest font-display mb-2">No records</div>
                  <div className="text-xs">Click <span className="text-[#3b4872] font-semibold">New</span> to add the first entry.</div>
                </td></tr>
              )}
              {!loading && items.map(row => (
                <tr key={row.id} data-testid={`${endpoint}-row`}>
                  {columns.map(c => <td key={c.key}>{renderCell(c, row)}</td>)}
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => editItem(row)} className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-[#3b4872] hover:bg-indigo-50 rounded-sm"><Edit size={13} /></button>
                      <button data-testid={`${endpoint}-delete-btn`} onClick={() => remove(row.id)} className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{editing ? 'Edit' : 'New'}</div>
                <h3 className="font-display font-bold text-xl mt-1">{title}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              {fields.filter(f => !(formId && f.cfg && ff.isHidden && ff.showField && !ff.showField(f.cfg))).map(f => {
                const required = formId && f.cfg ? ff.isReq(f.cfg, f.required || false) : !!f.required;
                const labelText = formId && f.cfg ? ff.labelOf(f.cfg, f.label) : f.label;
                const helpText = formId && f.cfg ? ff.helpOf(f.cfg) : null;
                return (
                <div key={f.key} className={f.full ? 'md:col-span-2' : ''}>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                    {labelText}{required && <span className="text-rose-500 ml-0.5">*</span>}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea
                      value={form[f.key] || ''}
                      onChange={e => setForm({...form, [f.key]: e.target.value})}
                      required={required}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
                    />
                  ) : f.type === 'select' ? (
                    <select
                      value={form[f.key] || ''}
                      onChange={e => setForm({...form, [f.key]: e.target.value})}
                      required={required}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
                    >
                      <option value="">—</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={f.type || 'text'}
                      value={form[f.key] || ''}
                      onChange={e => setForm({...form, [f.key]: e.target.value})}
                      required={required}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
                    />
                  )}
                  {helpText && <div className="text-[10px] text-slate-500 mt-1">{helpText}</div>}
                </div>
                );
              })}
              <div className="md:col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                <button data-testid={`${endpoint}-save-btn`} type="submit" className="px-4 py-2 text-sm bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-semibold">{editing ? 'Save' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
